<template>
  <div class="birth-place-field">
    <v-text-field
      :value="text"
      :label="label"
      :placeholder="placeholder"
      :rules="rules"
      :hint="hint"
      :persistent-hint="!!hint"
      :hide-details="hideDetails && !hint"
      outlined
      dense
      autocomplete="off"
      @input="onInput"
      @focus="open = predictions.length > 0"
      @blur="close"
    >
      <template #append>
        <v-progress-circular
          v-if="searching"
          indeterminate
          size="18"
          width="2"
          color="primary"
        ></v-progress-circular>
        <v-icon
          v-else-if="coordinates"
          small
          color="success"
          :title="$t('Coordinates from the place lookup')"
        >
          mdi-map-marker-check
        </v-icon>
      </template>
    </v-text-field>
    <div v-if="open" class="birth-place-dropdown">
      <button
        v-for="prediction in predictions"
        :key="prediction.place_id"
        type="button"
        class="birth-place-item"
        @mousedown.prevent="pick(prediction)"
      >
        <span class="d-block">{{ prediction.structured_formatting.main_text }}</span>
        <span class="caption grey--text">{{
          prediction.structured_formatting.secondary_text
        }}</span>
      </button>
    </div>
  </div>
</template>

<script>
  import { Loader } from '@googlemaps/js-api-loader'
  import { toPlace } from './birthPlace'

  /**
   * Google Places, loaded once for every field. Same loader options as
   * SelectLocation: the loader is a singleton and refuses different ones.
   * Without an API key (local dev) there is no lookup; the field is plain text.
   */
  let places = null
  const loadPlaces = () => {
    const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY
    if (!apiKey) return Promise.resolve(null)
    if (!places) {
      places = new Loader({ apiKey, libraries: ['places'] })
        .load()
        .then((google) => ({
          autocomplete: new google.maps.places.AutocompleteService(),
          details: new google.maps.places.PlacesService(document.createElement('div')),
        }))
        .catch((error) => {
          console.log('Error initializing Google Maps services:', error)
          return null
        })
    }
    return places
  }

  /**
   * A birthplace input: staff type the place and pick it from the Google
   * lookup, which adds the coordinates the wheel chart and the generated text
   * need. Typed text alone is still a value (`{ fullAddress }`, no coordinates).
   * v-model: `{ fullAddress, coordinates? }` or null.
   */
  export default {
    name: 'BirthPlaceField',
    props: {
      value: { type: [Object, String], default: null },
      label: { type: String, default: '' },
      placeholder: { type: String, default: '' },
      rules: { type: Array, default: () => [] },
      hideDetails: { type: Boolean, default: false },
    },
    data() {
      return {
        lookup: null,
        lookupReady: false,
        predictions: [],
        open: false,
        searching: false,
        timer: null,
        query: 0,
      }
    },
    computed: {
      place() {
        return toPlace(this.value)
      },
      text() {
        return this.place ? this.place.fullAddress : ''
      },
      coordinates() {
        return this.place && this.place.coordinates
      },
      hint() {
        if (!this.text || this.coordinates || !this.lookupReady) return ''
        return this.lookup
          ? this.$t('Pick the place from the list — without coordinates there is no chart')
          : this.$t('Place lookup unavailable — this place has no coordinates')
      },
    },
    async mounted() {
      this.lookup = await loadPlaces()
      this.lookupReady = true
    },
    beforeDestroy() {
      clearTimeout(this.timer)
    },
    methods: {
      onInput(text) {
        // Typing replaces a picked place: its coordinates belong to the old text.
        this.$emit('input', text ? { fullAddress: text } : null)
        clearTimeout(this.timer)
        if (!this.lookup || !text || !text.trim()) {
          this.predictions = []
          this.open = false
          return
        }
        this.timer = setTimeout(() => this.search(text.trim()), 300)
      },
      search(input) {
        const query = ++this.query
        this.searching = true
        this.lookup.autocomplete.getPlacePredictions({ input }, (results, status) => {
          if (query !== this.query) return
          this.searching = false
          this.predictions =
            status === 'OK' ? (results || []).filter((p) => p.structured_formatting) : []
          this.open = this.predictions.length > 0
        })
      },
      pick(prediction) {
        this.open = false
        this.lookup.details.getDetails(
          { placeId: prediction.place_id, fields: ['geometry', 'formatted_address', 'name'] },
          (place, status) => {
            if (status !== 'OK' || !place || !place.geometry || !place.geometry.location) return
            const round = (n) => Math.round(n * 1e6) / 1e6
            this.$emit('input', {
              fullAddress: prediction.description || place.formatted_address || place.name,
              coordinates: {
                lat: round(place.geometry.location.lat()),
                lng: round(place.geometry.location.lng()),
              },
            })
            this.predictions = []
          }
        )
      },
      close() {
        this.open = false
      },
    },
  }
</script>

<style scoped>
  .birth-place-field {
    position: relative;
    width: 100%;
  }

  .birth-place-dropdown {
    position: absolute;
    top: 40px;
    left: 0;
    right: 0;
    z-index: 10;
    max-height: 280px;
    overflow-y: auto;
    background-color: #1c1c1e;
    border: 1px solid #2c2c2e;
    border-radius: 0 0 4px 4px;
  }

  .birth-place-item {
    width: 100%;
    padding: 8px 12px;
    text-align: left;
    color: white;
    border-bottom: 1px solid #2c2c2e;
  }

  .birth-place-item:hover {
    background-color: #18273c;
  }
</style>
